import React, {useCallback, useEffect, useRef, useState} from "react";
import {FiEdit2, FiMessageCircle, FiPlus, FiSearch, FiTrash2, FiX} from "react-icons/fi";
import {useAIStateContext} from "../../../contexts/ai_chat_state_context";
import {useGroupContext} from "../../../contexts/group_context";
import {deleteChat, searchChats, updateChatTitle} from "../../../api";
import {ChatListItem, ChatSearchResult} from "../../../schemas/chat";
import Swal from "sweetalert2";

interface TabsProps {
    onNewChat: () => void;
}

const Tabs: React.FC<TabsProps> = ({onNewChat}) => {
    const {chats, setChats, currentChat, setCurrentChat} = useAIStateContext()!;
    const {currentGroup} = useGroupContext()!;

    const [overlayOpen, setOverlayOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState<ChatSearchResult[]>([]);
    const [searching, setSearching] = useState(false);
    const [editingChatId, setEditingChatId] = useState<string | null>(null);
    const [editTitle, setEditTitle] = useState("");

    const overlayRef = useRef<HTMLDivElement>(null);
    const overlaySearchRef = useRef<HTMLInputElement>(null);
    const editInputRef = useRef<HTMLInputElement>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout>>();

    // Close overlay on outside click
    useEffect(() => {
        if (!overlayOpen) return;
        const onClick = (e: MouseEvent) => {
            if (overlayRef.current && !overlayRef.current.contains(e.target as Node)) {
                closeOverlay();
            }
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [overlayOpen]);

    // Close on Escape
    useEffect(() => {
        if (!overlayOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                if (editingChatId) {
                    setEditingChatId(null);
                } else {
                    closeOverlay();
                }
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [overlayOpen, editingChatId]);

    // Focus edit input when entering edit mode
    useEffect(() => {
        if (editingChatId) {
            setTimeout(() => editInputRef.current?.focus(), 50);
        }
    }, [editingChatId]);

    const closeOverlay = () => {
        setOverlayOpen(false);
        setSearchQuery("");
        setSearchResults([]);
        setEditingChatId(null);
    };

    const openOverlay = () => {
        setOverlayOpen(true);
        setTimeout(() => overlaySearchRef.current?.focus(), 80);
    };

    const doSearch = useCallback(async (q: string) => {
        if (!q.trim() || !currentGroup) {
            setSearchResults([]);
            setSearching(false);
            return;
        }
        setSearching(true);
        try {
            const results = await searchChats(currentGroup.id, q.trim());
            setSearchResults(results);
        } catch (err) {
            console.error("Chat search error:", err);
            setSearchResults([]);
        } finally {
            setSearching(false);
        }
    }, [currentGroup]);

    const handleSearchInput = (value: string) => {
        setSearchQuery(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => doSearch(value), 300);
    };

    const selectChat = (chat: ChatListItem) => {
        setCurrentChat(chat);
        closeOverlay();
    };

    const selectSearchResult = (result: ChatSearchResult) => {
        const chat = chats.find(c => c.id === result.chat_id);
        if (chat) setCurrentChat(chat);
        closeOverlay();
    };

    // ---- Rename ----
    const startRename = (e: React.MouseEvent, chat: ChatListItem) => {
        e.stopPropagation();
        setEditingChatId(chat.id);
        setEditTitle(chat.title || "");
    };

    const commitRename = async () => {
        if (!editingChatId) return;
        const trimmed = editTitle.trim();
        if (!trimmed) {
            setEditingChatId(null);
            return;
        }
        try {
            const updated = await updateChatTitle(editingChatId, trimmed);
            setChats(prev => prev.map(c => c.id === editingChatId ? {...c, title: updated.title} : c));
            if (currentChat?.id === editingChatId) {
                setCurrentChat(prev => prev ? {...prev, title: updated.title} : prev);
            }
        } catch (err) {
            console.error("Rename failed:", err);
        }
        setEditingChatId(null);
    };

    const handleRenameKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            e.preventDefault();
            commitRename();
        } else if (e.key === "Escape") {
            setEditingChatId(null);
        }
    };

    // ---- Delete ----
    const handleDelete = async (e: React.MouseEvent, chat: ChatListItem) => {
        e.stopPropagation();
        const result = await Swal.fire({
            title: "Delete chat?",
            html: `<p style="font-size:14px;color:#555">This will permanently delete <b>&ldquo;${chat.title || "Untitled"}&rdquo;</b> and all associated:</p>
                   <ul style="font-size:13px;color:#666;text-align:left;margin-top:8px;list-style:disc;padding-left:20px">
                     <li>Document requests</li>
                     <li>References</li>
                     <li>Chat messages</li>
                   </ul>
                   <p style="font-size:13px;color:#888;margin-top:8px">Tags and associations will be kept but unlinked.</p>`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#e74c3c",
            confirmButtonText: "Delete",
            cancelButtonText: "Cancel",
        });

        if (!result.isConfirmed) return;

        try {
            await deleteChat(chat.id);
            setChats(prev => prev.filter(c => c.id !== chat.id));
            if (currentChat?.id === chat.id) {
                const remaining = chats.filter(c => c.id !== chat.id);
                setCurrentChat(remaining.length > 0 ? remaining[0] : null);
            }
        } catch (err) {
            console.error("Delete failed:", err);
            Swal.fire("Error", "Failed to delete chat.", "error");
        }
    };

    const isSearchMode = searchQuery.trim().length > 0;

    return (
        <div className="relative w-full" ref={overlayRef}>
            {/* ---- Row 1: Horizontal scrollable tabs ---- */}
            <div className="flex items-center overflow-x-auto overflow-y-hidden scrollbar-hide px-2 py-1.5 gap-1 border-b border-zinc-200">
                {chats.map((chat) => {
                    const isActive = currentChat?.id === chat.id;
                    return (
                        <button
                            key={chat.id}
                            onClick={() => setCurrentChat(chat)}
                            title={chat.title || "Untitled"}
                            className={[
                                "flex items-center gap-1.5 px-2.5 py-[4px] rounded-md text-[11px] font-medium whitespace-nowrap shrink-0 transition-all",
                                isActive
                                    ? "bg-[#ececf3] text-[#4e4e64] shadow-sm ring-1 ring-[#c4b5fd]/40"
                                    : "text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600",
                            ].join(" ")}
                        >
                            <FiMessageCircle className={`w-2.5 h-2.5 shrink-0 ${isActive ? "text-[#8c8ca3]" : "opacity-60"}`}/>
                            <span className="truncate max-w-[120px]">{chat.title || "Untitled"}</span>
                        </button>
                    );
                })}
                {chats.length === 0 && (
                    <span className="text-[11px] text-zinc-400 px-1">No chats</span>
                )}
            </div>

            {/* ---- Row 2: Search + New ---- */}
            <div className="flex items-center gap-1.5 px-2 py-1 border-b border-zinc-200 bg-zinc-50">
                <button
                    onClick={openOverlay}
                    className="flex items-center gap-1.5 flex-1 px-2 py-[3px] rounded-md text-[11px] text-zinc-400 hover:bg-zinc-100 hover:text-zinc-500 transition-all text-left"
                >
                    <FiSearch className="w-3 h-3 shrink-0"/>
                    <span>Search chats&hellip;</span>
                </button>

                <button
                    onClick={onNewChat}
                    title="New chat"
                    className="flex items-center gap-1 px-2 py-[3px] rounded-md text-[11px] font-medium text-[#6d6d8a] hover:bg-[#ececf3] transition-all shrink-0"
                >
                    <FiPlus className="w-3 h-3"/>
                    <span>New</span>
                </button>
            </div>

            {/* ---- Overlay panel ---- */}
            {overlayOpen && (
                <div className="absolute left-0 right-0 top-full z-40 bg-white border border-zinc-200 rounded-b-xl shadow-xl overflow-hidden animate-fade-in-down">
                    {/* Search input */}
                    <div className="flex items-center gap-2 px-3 py-2.5 bg-zinc-50 border-b border-zinc-100">
                        <FiSearch className="w-3.5 h-3.5 text-zinc-400 shrink-0"/>
                        <input
                            ref={overlaySearchRef}
                            type="text"
                            value={searchQuery}
                            onChange={(e) => handleSearchInput(e.target.value)}
                            placeholder="Search by title or message content&hellip;"
                            className="flex-1 text-xs outline-none bg-transparent text-zinc-700 placeholder-zinc-400"
                        />
                        {searchQuery && (
                            <button
                                onClick={() => {
                                    setSearchQuery("");
                                    setSearchResults([]);
                                    overlaySearchRef.current?.focus();
                                }}
                                className="text-zinc-400 hover:text-zinc-600"
                            >
                                <FiX className="w-3.5 h-3.5"/>
                            </button>
                        )}
                    </div>

                    {/* Chat list / search results */}
                    <div className="max-h-[280px] overflow-y-auto">
                        {isSearchMode && searching && (
                            <div className="px-3 py-4 text-xs text-zinc-400 text-center">Searching&hellip;</div>
                        )}

                        {isSearchMode && !searching && searchResults.length === 0 && (
                            <div className="px-3 py-4 text-xs text-zinc-400 text-center">No chats found</div>
                        )}

                        {isSearchMode && searchResults.map((result) => (
                            <button
                                key={result.chat_id}
                                onClick={() => selectSearchResult(result)}
                                className="w-full flex items-start gap-2.5 px-3 py-2.5 text-left hover:bg-zinc-50 transition-colors border-b border-zinc-50 last:border-0"
                            >
                                <FiMessageCircle className="w-3.5 h-3.5 text-[#8c8ca3] mt-0.5 shrink-0"/>
                                <div className="min-w-0 flex-1">
                                    <div className="text-xs font-medium text-zinc-700 truncate">
                                        {result.title || "Untitled"}
                                    </div>
                                    {result.snippet && result.matched_on === "message" && (
                                        <div className="text-[11px] text-zinc-400 truncate mt-0.5">
                                            &ldquo;{result.snippet}&rdquo;
                                        </div>
                                    )}
                                </div>
                                <span className="text-[10px] text-zinc-300 uppercase tracking-wide shrink-0 mt-0.5">
                                    {result.matched_on}
                                </span>
                            </button>
                        ))}

                        {!isSearchMode && chats.map((chat) => {
                            const isActive = currentChat?.id === chat.id;
                            const isEditing = editingChatId === chat.id;

                            return (
                                <div
                                    key={chat.id}
                                    onClick={() => !isEditing && selectChat(chat)}
                                    className={[
                                        "group w-full flex items-center gap-2 px-3 py-2 text-left transition-colors border-b border-zinc-50 last:border-0 cursor-pointer",
                                        isActive ? "bg-[#ececf3]" : "hover:bg-zinc-50",
                                    ].join(" ")}
                                >
                                    <FiMessageCircle className={`w-3.5 h-3.5 shrink-0 ${isActive ? "text-[#8c8ca3]" : "text-zinc-400"}`}/>

                                    {isEditing ? (
                                        <input
                                            ref={editInputRef}
                                            value={editTitle}
                                            onChange={(e) => setEditTitle(e.target.value)}
                                            onKeyDown={handleRenameKeyDown}
                                            onBlur={commitRename}
                                            onClick={(e) => e.stopPropagation()}
                                            className="flex-1 text-xs bg-white border border-zinc-300 rounded px-1.5 py-0.5 outline-none focus:border-[#c4b5fd] text-zinc-700"
                                        />
                                    ) : (
                                        <span className={`text-xs truncate flex-1 ${isActive ? "font-semibold text-[#4e4e64]" : "text-zinc-600"}`}>
                                            {chat.title || "Untitled"}
                                        </span>
                                    )}

                                    {/* Action buttons — visible on hover */}
                                    {!isEditing && (
                                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                                            <button
                                                onClick={(e) => startRename(e, chat)}
                                                title="Rename"
                                                className="p-1 rounded hover:bg-zinc-200 text-zinc-400 hover:text-zinc-600 transition-colors"
                                            >
                                                <FiEdit2 className="w-3 h-3"/>
                                            </button>
                                            <button
                                                onClick={(e) => handleDelete(e, chat)}
                                                title="Delete"
                                                className="p-1 rounded hover:bg-red-50 text-zinc-400 hover:text-red-500 transition-colors"
                                            >
                                                <FiTrash2 className="w-3 h-3"/>
                                            </button>
                                        </div>
                                    )}

                                    {isActive && !isEditing && (
                                        <span className="w-1.5 h-1.5 rounded-full bg-[#c4b5fd] shrink-0"/>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Hide scrollbar CSS */}
            <style>{`
                .scrollbar-hide::-webkit-scrollbar { display: none; }
                .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
            `}</style>
        </div>
    );
};

export default Tabs;
